import React, { useEffect, useState } from 'react';
import { CalendarDays } from 'lucide-react';
import { getHolidays, isAbortedRequestError } from '@/lib/api';

function daysFromNow(iso) {
  try {
    const d = new Date(iso + 'T00:00:00Z');
    const now = new Date();
    return Math.round((d.getTime() - now.getTime()) / (24 * 3600 * 1000));
  } catch { return null; }
}

function formatDate(iso) {
  try {
    return new Date(iso + 'T00:00:00Z').toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  } catch { return iso; }
}

export default function HolidaysCard({ countryCode, countryName }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!countryCode) return;
    let alive = true;
    setLoading(true);
    setError(false);
    getHolidays(countryCode)
      .then((r) => { if (alive) setItems(r?.holidays || []); })
      .catch((e) => { if (alive && !isAbortedRequestError(e)) setError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [countryCode]);

  if (!countryCode) return null;

  // Filter to upcoming (not in the past) and limit to 5
  const upcoming = (items || [])
    .map((h) => ({ ...h, _days: daysFromNow(h.date) }))
    .filter((h) => h._days != null && h._days >= 0)
    .slice(0, 5);

  if (!loading && (error || upcoming.length === 0)) {
    // Hide silently if Calendarific has no data for this country
    return null;
  }

  return (
    <div className="puvio-card p-5" data-testid="holidays-card">
      <div className="flex items-start justify-between">
        <div>
          <p className="font-bold text-[11px] uppercase tracking-[0.22em] text-[var(--puvio-mint-deep)]">Public holidays</p>
          <p className="text-xs text-[var(--puvio-text-2)] mt-0.5 font-medium">{countryName || 'Coming up'}</p>
        </div>
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: '#E0E8FF' }}>
          <CalendarDays className="w-6 h-6 text-[#0F1A13]" />
        </div>
      </div>
      {loading ? (
        <div className="mt-4 space-y-2" aria-hidden>
          <div className="puvio-skeleton h-10 w-full" />
          <div className="puvio-skeleton h-10 w-full" />
          <div className="puvio-skeleton h-10 w-full" />
        </div>
      ) : (
        <ul className="mt-4 divide-y divide-[var(--puvio-border)]">
          {upcoming.map((h, i) => (
            <li key={i} className="flex items-center justify-between gap-3 py-2.5" data-testid={`holiday-${i}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-[#0F1A13] truncate">{h.name}</p>
                <p className="text-[11px] text-[var(--puvio-text-3)] font-medium">{formatDate(h.date)}</p>
              </div>
              <span
                className={`text-[11px] font-extrabold px-2.5 py-1 rounded-full ${
                  h._days <= 0 ? 'bg-[var(--puvio-coral-deep)] text-[#0F1A13]' :
                  h._days <= 7 ? 'bg-[var(--puvio-sun)] text-[var(--puvio-sun-text)]' :
                  'bg-[var(--puvio-bg-soft)] text-[var(--puvio-text-2)]'
                }`}
              >
                {h._days === 0 ? 'Today' : h._days === 1 ? 'Tomorrow' : `in ${h._days} d`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
